import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Add } from "@material-ui/icons";
import { Box, Grid } from "@material-ui/core";
import translate from "i18n/translate";
import { useIntl } from "react-intl";
import Create from "./Create/Create";
import Restore from "./Restore/Restore";
import Update from "./Update/Update";
import Delete from "./Delete/Delete";
import useApplication from "./useApplication";
import ApplicationItem from "./ApplicationItem";
import View from "../../../utils/View";
import {
  SrButton,
  Typography,
  SrContentHeader,
  SrContentWrapper,
} from "../../../common";

const Applications = () => {
  const {
    error,
    alertError,
    clearAlertError,
    loading,
    loadingReset,
    applications,
    createApplication,
    updateApplication,
    deleteApplication,
    resetSecret,
  } = useApplication();
  const intl = useIntl();
  const [showCreate, setShowCreate] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const [btnDelete, setBtnDelete] = useState(false);
  const [secretKey, setSecretKey] = useState(undefined);
  const [selectedItem, setSelectedItem] = useState({});

  useEffect(() => {
    dispatchError(error);
  }, [error]);

  const handleDelete = async (key) => {
    setBtnDelete(true);
    const deleted = await deleteApplication(key);

    if (deleted) {
      toast.success(intl.formatMessage({ id: "applications.deleted" }));
      setShowDelete(false);
    }
    setBtnDelete(false);
  };

  const handleCreate = async (values) => {
    const response = await createApplication({
      application_name: values?.applicationName,
      application_bundle: values?.applicationBundle,
      platform: values.platform, //platform_id
      terms: values?.terms,
      oxxo: values?.oxxo,
      valid: values?.valid,
      msi: values?.msi,
    });

    if (response?.success) {
      setSecretKey(response?.result?.secret);
    }
  };

  const handleUpdate = async (data) => {
    clearAlertError();

    const response = await updateApplication(data?.key, {
      url: data?.form?.url,
      terms: data?.form?.terms,
      oxxo: data?.form?.oxxo,
      valid: data?.form?.oxxo ? data?.form?.valid.toString() : "0",
      msi: data?.form?.msi,
      notify: true,
    });

    if (response?.success) {
      toast.success(intl.formatMessage({ id: "applications.updated" }));
      setShowUpdate(false);
    }
  };

  const handleRestore = async (key, setShowForm) => {
    const response = await resetSecret(key);

    if (response?.success) {
      setSecretKey(response?.result?.secret);
      setShowForm(false);
    }
  };

  const handleShowDialog = (item, showDialog) => {
    setSelectedItem(item);
    showDialog(true);
  };

  const dispatchError = (msg) => {
    toast.error(msg);
  };

  return (
    <View translateTitleKey="menu.applications">
      <Grid container direction="column" spacing={2}>
        {showCreate && (
          <Create
            secretKey={secretKey}
            handleClose={() => {
              setShowCreate(false);
              setSecretKey(undefined);
            }}
            onSubmit={handleCreate}
            loading={loading}
            error={alertError}
            clearError={clearAlertError}
          />
        )}
        {showUpdate && (
          <Update
            handleClose={() => {
              setShowUpdate(false);
              clearAlertError();
            }}
            data={selectedItem}
            onSubmit={handleUpdate}
            loading={loading}
            error={alertError}
          />
        )}
        {showDelete && (
          <Delete
            handleClose={() => {
              setShowDelete(false);
              clearAlertError();
            }}
            data={selectedItem}
            onSubmit={handleDelete}
            loading={loading}
            isSubmitting={btnDelete}
            error={alertError}
          />
        )}
        {showRestore && (
          <Restore
            secretKey={secretKey}
            handleClose={() => {
              setShowRestore(false);
              setSecretKey(undefined);
              clearAlertError();
            }}
            data={selectedItem}
            loading={loadingReset}
            onSubmit={handleRestore}
            error={alertError}
          />
        )}

        <SrContentHeader
          title={translate("applications.title")}
          actions={
            <SrButton
              color="primary"
              variant="contained"
              onClick={() => setShowCreate(true)}
              startIcon={<Add />}
            >
              {translate("applications.newApp.field")}
            </SrButton>
          }
        />

        <SrContentWrapper
          isEmpty={!applications.length}
          isLoading={loading}
          emptyMessage={translate("applications.noAppsToShow")}
        >
          <Box
            marginTop="40px"
            width="100%"
            justifyContent="center"
            display="flex"
          >
            <Grid container justifyContent="center">
              {applications.length ? (
                applications.map((app) => (
                  <ApplicationItem
                    key={app.key}
                    data={app}
                    onEdit={() => handleShowDialog(app, setShowUpdate)}
                    onDelete={() => handleShowDialog(app, setShowDelete)}
                    onRestore={() => handleShowDialog(app, setShowRestore)}
                  />
                ))
              ) : (
                <Typography variant="h3">
                  {translate("applications.noAppsToShow")}
                </Typography>
              )}
            </Grid>
          </Box>
        </SrContentWrapper>
      </Grid>
    </View>
  );
};

export default Applications;
